import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Pagination from '../common/Pagination/Pagination';
import { useSuppliers } from '../../queries/supplierQueries';
import { useDeleteSupplier } from '../../mutations/supplierMutations';
import { extractItems, extractPagination } from '../../lib/apiHelpers';

const ViewSupplier = () => {
  const navigate = useNavigate();

  // ─── Local State ───────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());

  // Debounce search term changes to prevent spamming the API
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // ─── React Query: Fetch suppliers from API (server-side pagination & search) ──
  const { data: apiData, isLoading, isError } = useSuppliers(currentPage, debouncedSearch);
  const deleteMutation = useDeleteSupplier();

  // Unwrap the paginated envelope: { data:[], totalPages, ... }
  const suppliers  = useMemo(() => extractItems(apiData), [apiData]);
  const pagination = useMemo(() => extractPagination(apiData), [apiData]);
  const totalPages = pagination.totalPages;

  const paginatedSuppliers = suppliers; // server already paged and filtered


  // ─── Row Selection Logic ──────────────────────────────────────────────────
  // Prefer MongoDB _id; fall back to supplierId, then plain id (sample data)
  const getRowId = (s) => s.id;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIdsOnPage = paginatedSuppliers.map((s) => getRowId(s));
      setSelectedRowIds(new Set([...selectedRowIds, ...allIdsOnPage]));
    } else {
      const newSelected = new Set(selectedRowIds);
      paginatedSuppliers.forEach((s) => newSelected.delete(getRowId(s)));
      setSelectedRowIds(newSelected);
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRowIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRowIds(newSelected);
  };

  const isAllSelectedOnPage = useMemo(() => {
    if (paginatedSuppliers.length === 0) return false;
    return paginatedSuppliers.every((s) => selectedRowIds.has(getRowId(s)));
  }, [paginatedSuppliers, selectedRowIds]);

  // ─── Delete Selected Rows ──────────────────────────────────────────────────
  const handleDeleteSelected = () => {
    const idsToDelete = Array.from(selectedRowIds);
    if (idsToDelete.length === 0) return;

    Promise.all(idsToDelete.map((id) => deleteMutation.mutateAsync(id)))
      .then(() => {
        setSelectedRowIds(new Set()); // clear selection after delete
      })
      .catch(() => {
        // errors are already toasted by axiosInstance + mutation onError
      });
  };

  return (
    <div className="d-flex flex-column w-100 h-100">
      {/* ══ Enterprise Toolbar ══ */}
      <div className="gtss-toolbar-strip">

        {/* ── Left: Module Title ── */}
        <div className="d-flex flex-column me-1" style={{ flexShrink: 0 }}>
          <span className="gtss-page-title">Supplier Management</span>
          <span className="gtss-page-subtitle">Supplier Records</span>
        </div>

        {/* ── Divider ── */}
        <div className="gtss-divider"></div>

        {/* ── Search ── */}
        <div className="input-group" style={{ width: '240px' }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search by Name, ID..."
            aria-label="Search by Name or ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="input-group-text">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
            </svg>
          </span>
        </div>

        {/* ── Right Action Buttons ── */}
        <div className="d-flex align-items-center gap-2 ms-auto">
          {/* Delete Button (visible only when rows selected) */}
          {selectedRowIds.size > 0 && (
            <button
              className="btn btn-sm btn-danger"
              onClick={handleDeleteSelected}
              disabled={deleteMutation.isPending}
              aria-label={`Delete ${selectedRowIds.size} selected supplier(s)`}
            >
              {deleteMutation.isPending ? (
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16" className="me-1">
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                  <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                </svg>
              )}
              Delete ({selectedRowIds.size})
            </button>
          )}

          {/* Add Supplier Button */}
          <button
            className="btn btn-orange"
            onClick={() => navigate('/supplier/add')}
            aria-label="Add new supplier"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
            </svg>
            New Supplier
          </button>
        </div>

      </div>

      {/* ── Table Container — fills remaining height ── */}
      <div className="flex-grow-1 overflow-hidden d-flex flex-column gtss-view-container">
        <div className="border rounded-3 overflow-hidden bg-white d-flex flex-column flex-grow-1">
          {isError && (
            <div className="alert alert-danger mb-0 rounded-0 py-2 text-center" role="alert" style={{ fontSize: '14px' }}>
              <strong className="d-block d-sm-inline">Failed to load suppliers.</strong> Please try again later.
            </div>
          )}
          <div className="table-responsive flex-grow-1">
            <table className="table table-hover align-middle mb-0 gtss-table">

              {/* Header */}
              <thead className="gtss-bg-primary text-white text-uppercase">
                <tr>
                  <th className="py-2 px-2 text-center" style={{ width: '60px' }}>
                    <div className="d-flex align-items-center justify-content-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        aria-label="Select all suppliers on page"
                        checked={isAllSelectedOnPage}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  <th className="py-2 text-center">S.No</th>
                  <th className="py-2 text-start">Supplier Name</th>
                  <th className="py-2 text-start">Owner Name</th>
                  <th className="py-2 text-center">Mobile Number</th>
                  <th className="py-2 text-start">Email ID</th>
                </tr>
              </thead>

              {/* Body */}
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="py-5 text-center">
                      <div className="d-flex justify-content-center align-items-center gap-2">
                        <div className="spinner-border spinner-border-sm" role="status" style={{ color: '#1B4F8A' }}>
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="text-muted">Loading suppliers...</span>
                      </div>
                    </td>
                  </tr>
                ) : paginatedSuppliers.length > 0 ? (
                  paginatedSuppliers.map((supplier, idx) => {
                    const serialNo = (currentPage - 1) * pagination.pageSize + idx + 1;
                    const rowId = getRowId(supplier);
                    const isSelected = selectedRowIds.has(rowId);
                    return (
                      <tr
                        key={rowId}
                        className={isSelected ? 'table-primary-subtle' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate('/supplier/add', { state: supplier })}
                      >
                        <td className="px-2 text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex align-items-center justify-content-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              aria-label={`Select ${supplier.supplierName}`}
                              checked={isSelected}
                              onChange={() => handleSelectRow(rowId)}
                            />
                          </div>
                        </td>
                        <td className="text-center">{serialNo}</td>
                        <td className="text-start">{supplier.supplierName}</td>
                        <td className="text-start">{supplier.ownerName}</td>
                        <td className="text-center">{supplier.mobileNumber}</td>
                        <td className="text-start">{supplier.emailId}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="py-5 text-center text-muted">
                      <div className="d-flex flex-column align-items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="text-secondary" viewBox="0 0 16 16">
                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                          <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z"/>
                        </svg>
                        <span>No data found.</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>


            </table>
          </div>

          {/* ── Pagination Footer ── */}
          {totalPages >= 1 && (
            <div className="border-top bg-white py-3">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ViewSupplier;